import React, { useState, useContext, useEffect } from "react";
import "../Styles/UpdatePassword.css";
// ✅ CORRECT: Import OTPHandler component separately
import OTPHandler from "../../../../common/OTPHandler/otphandler";
// ✅ CORRECT: Import OTP service functions from the service layer
import { requestOTP, validateOTP } from "../../../../api/auth/otpService";
import { ThemeContext } from "../../../../../context/ThemeContext";
import { USE_MOCK_DATA } from "../../../../config/apiConfig";
import { toast } from "react-toastify";

const UpdatePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [mobileNumber, setMobileNumber] = useState(""); // ✅ Changed from const to allow input
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    // Dynamically import the correct theme CSS
    import(`../Styles/themes/${theme}/themes.css`)
      .then(() => {
        console.log(`Loaded ${theme} styles for UpdatePassword`);
      })
      .catch(() => {
        console.error(`Theme CSS not found for ${theme}`);
      });
  }, [theme]);

  /**
   * ✅ Handle OTP Verification Success
   * Called by OTPHandler component when OTP is verified
   */
  const handleOTPVerified = (data) => {
    console.log('✅ OTP Verified:', data);
    setIsOTPVerified(true);
    setMessage("Mobile number verified! You can now update your password.");
    toast.success("OTP Verified successfully!");
    
    // Store device ID if provided
    if (data.deviceId) {
      localStorage.setItem('deviceId', data.deviceId);
    }
  };

  /**
   * ✅ Handle Password Change
   */
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage("All fields are required.");
      toast.error("All fields are required.");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("New password must be at least 8 characters long.");
      toast.error("New password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("New password and confirm password do not match.");
      toast.error("Passwords do not match.");
      return;
    }

    if (!isOTPVerified) {
      setMessage("Please verify your mobile number with OTP first.");
      toast.error("Please verify OTP first.");
      return;
    }

    try {
      const response = await fetch("/api/update-password", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          // Add auth token if required
          "Authorization": `Bearer ${localStorage.getItem('securityToken')}`
        },
        body: JSON.stringify({ 
          currentPassword, 
          newPassword,
          deviceId: localStorage.getItem('deviceId')
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage("Password updated successfully!");
        toast.success("Password updated successfully! 🎉");
        
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setIsOTPVerified(false);
      } else {
        const errorMsg = data.error || "Failed to update password.";
        setMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg = "An error occurred. Please try again.";
      setMessage(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Password update error:', error);
    }
  };

  return (
    <div className="st-update-password-container">
      <h2 className="st-text-header" style={{ marginTop: 30 }}>
        Update Password
      </h2>

      {/* ✅ Mock Mode Banner */}
      {USE_MOCK_DATA && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '10px',
          borderRadius: '5px',
          margin: '10px',
          textAlign: 'center',
          fontWeight: '600',
        }}>
          🎭 MOCK MODE: Any 6-digit OTP will work for testing
        </div>
      )}

      {message && (
        <p style={{ 
          color: message.includes("success") ? "green" : "red",
          margin: '10px',
          fontWeight: '600',
        }}>
          {message}
        </p>
      )}

      <form onSubmit={handlePasswordChange}>
        {/* Current Password */}
        <label className="st-text-label" style={{ marginLeft: 10, marginTop: 10 }}>
          Current Password:
        </label>
        <div>
          <input
            className="st-password-text-container"
            style={{ width: "20%", background: "gray", color: "black", marginTop: -10 }}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>

        {/* New Password */}
        <label className="st-text-label" style={{ marginLeft: 10, marginTop: 20 }}>
          New Password:
        </label>
        <div style={{ marginTop: 10 }}>
          <input
            type="password"
            className="st-password-text-container"
            style={{ width: "20%", background: "gray", color: "black", marginTop: -20 }}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>

        {/* Confirm New Password */}
        <label className="st-text-label" style={{ marginLeft: 10, marginTop: 20 }}>
          Confirm New Password:
        </label>
        <div style={{ marginTop: 0 }}>
          <input
            type="password"
            className="st-password-text-container"
            value={confirmPassword}
            style={{ width: "20%", background: "gray", color: "black", marginTop: -20 }}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {/* ✅ OTP Handler Component */}
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <OTPHandler 
            onOTPVerified={handleOTPVerified}
            initialMobile={mobileNumber}
            autoOpenModal={false}
          />
        </div>

        {/* OTP Verification Status */}
        {isOTPVerified && (
          <div style={{
            backgroundColor: 'rgba(36,36,45,0.2)',
            color: '#155724',
            padding: '10px',
            borderRadius: '5px',
            margin: '10px',
            textAlign: 'center',
            fontWeight: '600',
          }}>
            ✅ Mobile number verified successfully!
          </div>
        )}

        {/* Submit Button */}
        <button
          className="st-submit-button"
          type="submit"
          disabled={!isOTPVerified}
          style={{
            background: isOTPVerified ? "blue" : "gray",
            borderRadius: 10,
            cursor: isOTPVerified ? "pointer" : "not-allowed",
            opacity: isOTPVerified ? 1 : 0.6,
            marginLeft: 10,
            marginTop: 20,
          }}
        >
          Update Password
        </button>
      </form>
    </div>
  );
};

export default UpdatePassword;
